import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import { uniqBy } from 'lodash'
import { Button, Empty, Select } from 'antd'
import {
  SelectInputStyled,
  notification,
  useDropdownData,
} from '@edulastic/common'
import { tagsApi } from '@edulastic/api'
import { accessibilityColors } from '@edulastic/colors'
import styled from 'styled-components'
import { IconClose } from '@edulastic/icons'
import {
  getAllTagsAction,
  getAllTagsSelector,
  addNewTagAction,
} from '../../../TestPage/ducks'
import { setSelectSearchAriaLabel } from '../../../../common/utils/helpers'

const TagFilter = ({
  selectedTags = [],
  testTagList = [],
  onChangeField = () => {},
  addNewTag = () => {},
  canCreate = false,
  getAllTags = () => {},
  margin = '0px',
  tagType = 'assignment',
  selectedTagIds,
  labelId,
}) => {
  let selectedTagObjects = selectedTags
  if (selectedTagIds && !selectedTags.length) {
    selectedTagObjects = testTagList.filter(({ _id }) =>
      selectedTagIds.includes(_id)
    )
  }
  const newAllTagsData = uniqBy([...testTagList, ...selectedTagObjects], '_id')
  const [searchValue, setSearchValue] = useState('')
  const [searchText, setSearchText] = useState('')
  const [popupOpen, setPopupOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(0)

  useEffect(() => {
    getAllTags({ type: ['test', 'assignment'] })
  }, [])

  const selectTags = async (id) => {
    let newTag = {}
    if (id === searchValue) {
      const tempSearchValue = searchValue
      setSearchValue('')
      try {
        const { _id, tagName } = await tagsApi.create({
          tagName: tempSearchValue,
          tagType,
        })
        newTag = { _id, tagName }
        addNewTag({ tag: newTag, tagType: 'test' })
      } catch (e) {
        notification({ messageKey: 'savingTagErr' })
      }
    } else {
      newTag = newAllTagsData.find((tag) => tag._id === id)
    }
    const newTags = [...selectedTagObjects, newTag]
    onChangeField('tags', newTags)
    setSearchValue('')
    setSearchText('')
  }

  const deselectTags = (id) => {
    const newTags = selectedTagObjects.filter((tag) => tag._id !== id)
    onChangeField('tags', newTags)
  }

  const searchTags = async (value) => {
    setSearchText(value)
    if (!canCreate) return

    if (
      newAllTagsData.some(
        (tag) =>
          tag.tagName.toLowerCase() === value.toLowerCase() ||
          tag.tagName.toLowerCase() === value.trim().toLowerCase()
      )
    ) {
      setSearchValue('')
    } else {
      setSearchValue(value)
    }
  }
  const onBlur = useCallback(() => {
    setSearchText('')
    setPopupOpen(false)
  }, [])
  const _selectedTagIds = useMemo(() => selectedTagObjects.map((t) => t._id), [
    selectedTagObjects,
  ])

  const getOptionId = (idx) => `tag-filter-option-${idx}`

  const options = useDropdownData(newAllTagsData, {
    title_key: 'tagName',
    id_key: '_id',
    value_key: '_id',
    searchText,
    OptionComponent: Select.Option,
    getOptionId,
    optionProps: {
      role: 'menuitem',
      tabIndex: 0,
    },
  })

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setFocusedIndex((prev) => (prev + 1 >= options.length ? 0 : prev + 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setFocusedIndex((prev) =>
          prev - 1 < 0 ? options.length - 1 : prev - 1
        )
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        setPopupOpen(true)
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setPopupOpen(false)
      }
    },
    [popupOpen, options, setFocusedIndex]
  )

  return (
    <SelectInputStyled
      showArrow
      data-cy="tagsSelect"
      className="tagsSelect"
      mode="multiple"
      optionLabelProp="title"
      placeholder="Please select"
      open={popupOpen}
      value={_selectedTagIds}
      onSearch={searchTags}
      onSelect={selectTags}
      onDeselect={deselectTags}
      onBlur={onBlur}
      onInputKeyDown={handleKeyDown}
      onDropdownVisibleChange={() => setPopupOpen(!popupOpen)}
      getPopupContainer={(trigger) => trigger.parentNode}
      filterOption={(input, option) =>
        option.props.title.toLowerCase().includes(input.trim().toLowerCase())
      }
      removeIcon={
        <StyledButton tabIndex={0} aria-label="Remove Search Tag">
          <StyledIconClose aria-hidden="true" />
        </StyledButton>
      }
      margin={margin}
      notFoundContent={
        <Empty
          className="ant-empty-small"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ textAlign: 'left', margin: '10px 0' }}
          description="No matching results"
        />
      }
      ref={(ref) => {
        setSelectSearchAriaLabel(ref, 'Search Tags')
      }}
      id="tagsFilterInput"
      aria-labelledby={labelId}
      aria-haspopup="menu"
      aria-expanded={popupOpen}
      aria-activedescendant={getOptionId(focusedIndex)}
    >
      {searchValue?.trim() ? (
        <Select.Option key={0} value={searchValue} title={searchValue}>
          {`${searchValue} (Create new Tag)`}
        </Select.Option>
      ) : (
        ''
      )}
      {options}
    </SelectInputStyled>
  )
}

export default connect(
  (state) => ({
    testTagList: getAllTagsSelector(state, 'test'),
    assignmentTagList: getAllTagsSelector(state, 'assignment'),
  }),
  { getAllTags: getAllTagsAction, addNewTag: addNewTagAction }
)(TagFilter)

export const StyledButton = styled(Button)`
  width: fit-content;
  padding: 0px;
  height: 15px;
  background: none;
  outline: none;
  border: none;
  margin-top: 0px;
  margin-right: -3px;
  &:hover {
    background: none;
  }
  &:focus {
    outline: 2px solid ${accessibilityColors.theme.blue};
    background: none;
  }
`

export const StyledIconClose = styled(IconClose)`
  width: 10px;
  height: 10px !important;
`
